import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
} from "@mui/material";
import { DoneAll, OpenInNew, Send } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import useNotifications from "../../hooks/useNotifications";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import SendNotificationDialog from "../../components/common/SendNotificationDialog";

dayjs.extend(relativeTime);

// `bg` values are MUI theme token strings (resolved wherever they're used
// as an sx `bgcolor`), not literal hex — so each adapts to light/dark mode
// automatically instead of staying a fixed pastel.
const TYPE_ICONS = {
  shipment_update: { icon: "📦", bg: "info.light" },
  sailing_update: { icon: "⛵", bg: "rgba(147,51,234,0.14)" },
  payment_reminder: { icon: "💳", bg: "error.light" },
  customs_alert: { icon: "📋", bg: "warning.light" },
  document_required: { icon: "⚠️", bg: "warning.light" },
  document_uploaded: { icon: "📄", bg: "info.light" },
  pickup_ready: { icon: "🎉", bg: "success.light" },
  general: { icon: "🔔", bg: "action.hover" },
};

const AdminNotifications = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, load, read, readAll } =
    useNotifications();
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    load({ limit: 50 }).finally(() => setLoading(false));
  }, []);

  const filtered = notifications.filter((n) => {
    if (filter === "unread") return !n.isRead;
    if (filter === "read") return n.isRead;
    return true;
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Notifications"
        subtitle={unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
        action={
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button size="small" variant="contained" startIcon={<Send />} onClick={() => setSendOpen(true)}>
              Send Notification
            </Button>
            <TextField
              select
              size="small"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              sx={{ width: 130 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="unread">Unread</MenuItem>
              <MenuItem value="read">Read</MenuItem>
            </TextField>
            {unreadCount > 0 && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DoneAll />}
                onClick={readAll}
              >
                Mark All Read
              </Button>
            )}
          </Box>
        }
      />

      <Card>
        {loading ? (
          <SkeletonLoader rows={6} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="🔔"
            title="No notifications"
            description="System notifications will appear here."
          />
        ) : (
          filtered.map((n, idx) => {
            const cfg = TYPE_ICONS[n.type] || TYPE_ICONS.general;
            return (
              <Box key={n._id}>
                <Box
                  onClick={() => {
                    if (!n.isRead) read(n._id);
                  }}
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    px: 2.5,
                    py: 2,
                    bgcolor: n.isRead ? "transparent" : "rgba(13,98,155,0.03)",
                    cursor: "pointer",
                    transition: "background 0.1s",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: cfg.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {cfg.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{ fontSize: 13.5, fontWeight: n.isRead ? 500 : 700 }}
                    >
                      {n.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.25 }}
                    >
                      {n.message}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {dayjs(n.createdAt).fromNow()}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 1,
                      flexShrink: 0,
                    }}
                  >
                    {!n.isRead && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "primary.main",
                        }}
                      />
                    )}
                    {n.shipment && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/admin/shipments/${n.shipment.trackingNumber || n.shipment}`,
                          );
                        }}
                      >
                        <OpenInNew sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                {idx < filtered.length - 1 && <Divider />}
              </Box>
            );
          })
        )}
      </Card>

      <SendNotificationDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        onSent={() => load({ limit: 50 })}
      />
    </Box>
  );
};

export default AdminNotifications;
